import React, { useCallback, useEffect, useRef, useState } from 'react';
import { describeElement, detectScreen, resolveInteractiveTarget } from './selectorEngine';

/**
 * Opt-in tool for authoring tutorials by demonstration.
 *
 * Walk the real flow once with this running and it captures every click, input
 * and screen change, then exports a session JSON containing draft tutorial
 * steps plus the list of elements that still need `data-tutorial` anchors.
 *
 * Enable with `?tutorialRecord=1`, or `window.tutorialRecorder.start()`.
 * It is inert unless explicitly enabled and never calls preventDefault, so the
 * app behaves exactly as it would without it.
 *
 * A released build can't show it at all, whatever the URL says — set
 * REACT_APP_TUTORIAL_RECORDER=1 at build time for the rare case of authoring
 * against a production bundle.
 */

const STORAGE_KEY = 'tutorialRecorder.session';
const ENABLED_KEY = 'tutorialRecorder.enabled';
const INPUT_DEBOUNCE_MS = 600;
const PANEL_Z_INDEX = 2147483000;

const RECORDER_AVAILABLE =
  process.env.NODE_ENV !== 'production' || process.env.REACT_APP_TUTORIAL_RECORDER === '1';

// Being enabled used to persist in localStorage, so the panel came back for
// days after a recording session — including over the app someone was trying to
// look at. Anything left from that is cleared here, and the flag now lasts only
// as long as the tab.
try {
  localStorage.removeItem(ENABLED_KEY);
} catch (e) {
  /* storage unavailable — nothing to clean up */
}

const readStoredSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const isRecorderEnabled = () => {
  if (!RECORDER_AVAILABLE) return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('tutorialRecord');
    if (fromUrl === '1' || fromUrl === 'true') {
      sessionStorage.setItem(ENABLED_KEY, '1');
      return true;
    }
    if (fromUrl === '0' || fromUrl === 'false') {
      sessionStorage.removeItem(ENABLED_KEY);
      return false;
    }
    return sessionStorage.getItem(ENABLED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const slugify = (value, fallback) => {
  const slug = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .split('-')
    .slice(0, 5)
    .join('-');
  return slug || fallback;
};

/** Shapes the raw capture log into something close to the real step schema. */
const buildDraftSteps = (events) =>
  events
    .filter((event) => event.type === 'click' || event.type === 'change' || event.type === 'input')
    .map((event, index) => ({
      id: `${index + 1}-${event.screen}-${slugify(event.element?.label, 'step')}`,
      chapter: event.screen,
      title: event.element?.label || 'Untitled step',
      description: event.note || '',
      target: event.element?.selector || null,
      needsAnchor: event.element ? !event.element.stable : true,
      anchorSuggestion: event.element?.anchorSuggestion || null,
      interaction: event.type,
      recordedValue: event.value ?? null,
      offsetPct: event.element?.offsetPct || null,
      // Canvas gestures replay from the design's coordinates rather than the
      // canvas position they were demonstrated at, so this is what a step's
      // `designPoints` are written from.
      designPoint: event.element?.designPoint || null,
      actionRequired: true,
      highlight: true,
    }));

/** The punch list of elements I need to add stable anchors to. */
const buildAnchorsNeeded = (events) => {
  const byPath = new Map();

  events.forEach((event) => {
    const el = event.element;
    if (!el || el.stable) return;

    const key = el.domPath;
    const existing = byPath.get(key);
    if (existing) {
      existing.count += 1;
      return;
    }

    byPath.set(key, {
      count: 1,
      screen: event.screen,
      label: el.label,
      tag: el.tag,
      classes: el.classes,
      domPath: el.domPath,
      suggestedAttribute: `data-tutorial="${el.anchorSuggestion}"`,
    });
  });

  return Array.from(byPath.values());
};

const TutorialRecorder = () => {
  const [enabled] = useState(isRecorderEnabled);
  const [recording, setRecording] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [events, setEvents] = useState(readStoredSession);

  const panelRef = useRef(null);
  const recordingRef = useRef(false);
  const pendingInputRef = useRef(new Map());
  const lastClickRef = useRef(null);

  recordingRef.current = recording;

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* session is still held in memory */
    }
  }, []);

  const append = useCallback(
    (event) => {
      setEvents((prev) => {
        const next = [...prev, { ...event, seq: prev.length + 1, at: Date.now() }];
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const isOwnEvent = useCallback((event) => {
    const panel = panelRef.current;
    if (!panel) return false;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path.includes(panel) || (event.target instanceof Node && panel.contains(event.target));
  }, []);

  // Click capture
  useEffect(() => {
    if (!enabled) return undefined;

    const onClick = (event) => {
      if (!recordingRef.current || isOwnEvent(event)) return;

      const el = resolveInteractiveTarget(event);
      if (!el) return;

      // Hidden proxies (Ant's file input, for one) get clicked programmatically
      // by the visible control the user actually pressed.
      const rect = el.getBoundingClientRect();
      if (!rect.width && !rect.height) return;

      // Components that re-dispatch a click land on a related node moments
      // later; the first hit is the one the user aimed at.
      const now = Date.now();
      const last = lastClickRef.current;
      if (
        last &&
        now - last.time < 400 &&
        (last.el === el || last.el.contains(el) || el.contains(last.el))
      ) {
        return;
      }
      lastClickRef.current = { el, time: now };

      // Focusing a field is not a distinct teaching step; the value change is.
      const isTextField =
        (el.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit'].includes(el.type)) ||
        el.tagName === 'TEXTAREA';
      if (isTextField) return;

      append({
        type: 'click',
        screen: detectScreen(),
        route: window.location.pathname + window.location.search,
        element: describeElement(el, event),
      });
    };

    window.addEventListener('click', onClick, { capture: true, passive: true });
    return () => window.removeEventListener('click', onClick, { capture: true });
  }, [enabled, append, isOwnEvent]);

  // Value capture: sliders and text inputs settle before we record them.
  useEffect(() => {
    if (!enabled) return undefined;

    const flush = (el, value) => {
      append({
        type: 'input',
        screen: detectScreen(),
        route: window.location.pathname + window.location.search,
        element: describeElement(el),
        value,
      });
    };

    const pending = pendingInputRef.current;

    const onInput = (event) => {
      if (!recordingRef.current || isOwnEvent(event)) return;

      const el = resolveInteractiveTarget(event);
      if (!el) return;

      clearTimeout(pending.get(el));
      pending.set(
        el,
        setTimeout(() => {
          pending.delete(el);
          flush(el, el.value);
        }, INPUT_DEBOUNCE_MS)
      );
    };

    const onChange = (event) => {
      if (!recordingRef.current || isOwnEvent(event)) return;

      const el = resolveInteractiveTarget(event);
      if (!el) return;
      if (el.tagName === 'INPUT' && (el.type === 'range' || el.type === 'text')) return;

      append({
        type: 'change',
        screen: detectScreen(),
        route: window.location.pathname + window.location.search,
        element: describeElement(el),
        value: el.type === 'checkbox' ? el.checked : el.value,
      });
    };

    window.addEventListener('input', onInput, { capture: true, passive: true });
    window.addEventListener('change', onChange, { capture: true, passive: true });

    return () => {
      window.removeEventListener('input', onInput, { capture: true });
      window.removeEventListener('change', onChange, { capture: true });
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, [enabled, append, isOwnEvent]);

  // Screen changes, including SPA transitions that never touch the URL.
  useEffect(() => {
    if (!enabled) return undefined;

    let lastScreen = detectScreen();

    const check = () => {
      if (!recordingRef.current) return;
      const screen = detectScreen();
      if (screen === lastScreen) return;

      const from = lastScreen;
      lastScreen = screen;
      append({
        type: 'screen',
        screen,
        from,
        route: window.location.pathname + window.location.search,
      });
    };

    const interval = setInterval(check, 700);
    window.addEventListener('popstate', check);

    return () => {
      clearInterval(interval);
      window.removeEventListener('popstate', check);
    };
  }, [enabled, append]);

  const handleExport = useCallback(() => {
    const payload = {
      version: 1,
      recordedAt: new Date().toISOString(),
      variant: process.env.REACT_APP_VARIANT || 'svgtrace',
      viewport: { width: window.innerWidth, height: window.innerHeight },
      eventCount: events.length,
      events,
      draftSteps: buildDraftSteps(events),
      anchorsNeeded: buildAnchorsNeeded(events),
    };

    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `tutorial-recording-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    if (navigator.clipboard) {
      navigator.clipboard.writeText(json).catch(() => {});
    }
    window.tutorialRecordingPayload = payload;
  }, [events]);

  const handleNote = useCallback(() => {
    // eslint-disable-next-line no-alert
    const note = window.prompt('Note for this point in the flow (what should the tutorial say here?)');
    if (!note) return;

    append({ type: 'note', screen: detectScreen(), note });
  }, [append]);

  const handleUndo = useCallback(() => {
    setEvents((prev) => {
      const next = prev.slice(0, -1);
      persist(next);
      return next;
    });
  }, [persist]);

  const handleClear = useCallback(() => {
    setEvents([]);
    persist([]);
  }, [persist]);

  // Console control, handy when the panel is in the way of the thing being clicked.
  useEffect(() => {
    if (!enabled) return undefined;

    window.tutorialRecorder = {
      start: () => setRecording(true),
      stop: () => setRecording(false),
      export: handleExport,
      clear: handleClear,
      events: () => events,
      disable: () => {
        sessionStorage.removeItem(ENABLED_KEY);
        window.location.reload();
      },
    };

    return () => {
      delete window.tutorialRecorder;
    };
  }, [enabled, handleExport, handleClear, events]);

  if (!enabled) return null;

  const unstableCount = events.filter((e) => e.element && !e.element.stable).length;
  const recent = events.slice(-6).reverse();

  const buttonStyle = {
    padding: '5px 9px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        right: '16px',
        bottom: '16px',
        zIndex: PANEL_Z_INDEX,
        width: collapsed ? 'auto' : '300px',
        background: 'rgba(17,17,24,0.94)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: '#fff',
        borderRadius: '10px',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 10px 34px rgba(0,0,0,0.45)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '12px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 10px',
          borderBottom: collapsed ? 'none' : '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <span
          style={{
            width: '9px',
            height: '9px',
            borderRadius: '50%',
            background: recording ? '#ff4d4f' : '#666',
            boxShadow: recording ? '0 0 0 4px rgba(255,77,79,0.22)' : 'none',
            flexShrink: 0,
          }}
        />
        <strong style={{ fontSize: '12px', flex: 1 }}>
          Tutorial Recorder
          <span style={{ opacity: 0.6, fontWeight: 400 }}> · {events.length}</span>
        </strong>
        <button type="button" onClick={() => setCollapsed((c) => !c)} style={{ ...buttonStyle, padding: '3px 7px' }}>
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {!collapsed && (
        <div style={{ padding: '10px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            <button
              type="button"
              onClick={() => setRecording((r) => !r)}
              style={{
                ...buttonStyle,
                background: recording ? '#ff4d4f' : '#F453C4',
                borderColor: 'transparent',
              }}
            >
              {recording ? 'Pause' : 'Record'}
            </button>
            <button type="button" onClick={handleNote} style={buttonStyle}>
              Note
            </button>
            <button type="button" onClick={handleUndo} style={buttonStyle} disabled={!events.length}>
              Undo
            </button>
            <button type="button" onClick={handleClear} style={buttonStyle} disabled={!events.length}>
              Clear
            </button>
            <button
              type="button"
              onClick={handleExport}
              style={{ ...buttonStyle, background: '#2f9e44', borderColor: 'transparent' }}
              disabled={!events.length}
            >
              Export
            </button>
          </div>

          {unstableCount > 0 && (
            <div
              style={{
                padding: '6px 8px',
                marginBottom: '8px',
                borderRadius: '6px',
                background: 'rgba(255,193,7,0.14)',
                border: '1px solid rgba(255,193,7,0.3)',
                color: '#ffd54f',
                fontSize: '11px',
              }}
            >
              {unstableCount} of {events.length} need a stable anchor — captured, I will add them.
            </div>
          )}

          <div style={{ maxHeight: '190px', overflowY: 'auto' }}>
            {recent.length === 0 && (
              <div style={{ opacity: 0.55, fontSize: '11px', lineHeight: 1.5 }}>
                Press Record, then run the full flow: upload the sample, trace it, open the stitch
                editor, set it up, and export the file. Use Note to leave a message for any step.
              </div>
            )}

            {recent.map((event) => (
              <div
                key={event.seq}
                style={{
                  display: 'flex',
                  gap: '7px',
                  padding: '5px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  alignItems: 'baseline',
                }}
              >
                <span style={{ opacity: 0.45, fontSize: '10px', minWidth: '18px' }}>{event.seq}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      opacity: 0.55,
                      marginRight: '5px',
                    }}
                  >
                    {event.type}
                  </span>
                  <span style={{ wordBreak: 'break-word' }}>
                    {event.note || event.element?.label || event.screen || '(no label)'}
                  </span>
                  {event.element && !event.element.stable && (
                    <span style={{ color: '#ffd54f', marginLeft: '5px' }} title="Needs a data-tutorial anchor">
                      ⚑
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialRecorder;
